import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, Mail, Phone, RefreshCw } from "lucide-react";
import { ICON_SIZE } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleString();
    } catch (error) {
        console.error("Invalid date", error);
        return iso;
    }
}

export default function AdminLeads() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/leads`);
            setLeads(r.data.leads || []);
        } catch (error) {
            console.error("Failed to load leads", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <div className="min-h-screen bg-cream grain-bg">
            <div className="relative z-10 max-w-5xl mx-auto px-6 sm:px-10 py-12">
                <Header count={leads.length} onRefresh={load} />
                <Body loading={loading} leads={leads} />
            </div>
        </div>
    );
}

function Header({ count, onRefresh }) {
    const label = count === 1 ? "lead" : "leads";
    return (
        <div className="flex items-center justify-between mb-10">
            <div>
                <div className="eyebrow">Admin · Captured Leads</div>
                <h1 className="serif-display text-5xl text-navy mt-2">Your Inbox</h1>
                <p className="text-muted2 mt-2">
                    {count} {label} captured from the Emerald Coast guide.
                </p>
            </div>
            <button
                onClick={onRefresh}
                data-testid="admin-refresh-btn"
                className="inline-flex items-center gap-2 border border-navy text-navy py-2.5 px-4 rounded-sm font-sans text-xs font-semibold tracking-[0.18em] uppercase transition-all hover:bg-navy hover:text-cream"
            >
                <RefreshCw size={ICON_SIZE.INLINE_MD} />
                Refresh
            </button>
        </div>
    );
}

function Body({ loading, leads }) {
    if (loading) {
        return (
            <div className="flex items-center justify-center py-32">
                <Loader2 className="animate-spin text-navy" />
            </div>
        );
    }
    if (leads.length === 0) {
        return (
            <div className="border border-hairline bg-white p-16 text-center">
                <div className="serif-display text-3xl text-navy">No leads yet.</div>
                <p className="text-muted2 mt-2">Share your guide and they'll show up here.</p>
            </div>
        );
    }
    return <LeadsTable leads={leads} />;
}

function LeadsTable({ leads }) {
    return (
        <div className="border border-hairline bg-white overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-navy text-cream">
                    <tr>
                        <Th>Name</Th>
                        <Th>Email</Th>
                        <Th>Phone</Th>
                        <Th>Captured</Th>
                    </tr>
                </thead>
                <tbody data-testid="admin-leads-table">
                    {leads.map((lead, i) => (
                        <LeadRow key={lead.id} lead={lead} zebra={i % 2 !== 0} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function Th({ children }) {
    return (
        <th className="text-left font-sans font-semibold tracking-[0.12em] uppercase text-[10px] px-5 py-4">
            {children}
        </th>
    );
}

function LeadRow({ lead, zebra }) {
    const rowBg = zebra ? "bg-sand/50" : "bg-white";
    return (
        <tr className={`${rowBg} border-t border-hairline`}>
            <td className="px-5 py-4 font-serif text-lg text-navy">{lead.name}</td>
            <td className="px-5 py-4">
                <a
                    href={`mailto:${lead.email}`}
                    className="inline-flex items-center gap-2 text-ink hover:text-coral break-all"
                >
                    <Mail size={ICON_SIZE.INLINE_SM} className="flex-shrink-0" />
                    {lead.email}
                </a>
            </td>
            <td className="px-5 py-4">
                <a
                    href={`tel:${lead.phone}`}
                    className="inline-flex items-center gap-2 text-ink hover:text-coral"
                >
                    <Phone size={ICON_SIZE.INLINE_SM} />
                    {lead.phone}
                </a>
            </td>
            <td className="px-5 py-4 text-muted2 text-xs">{formatDate(lead.created_at)}</td>
        </tr>
    );
}
